import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@/store/userStore';
import { useAppName } from '@/hooks/useAppName';
import { supabase } from '@/integrations/supabase/client';
import {
  parseCSV,
  detectColumnMapping,
  normalizePhoneNumber,
  isValidEmail,
  validateEmail,
  cleanName,
  parseDate,
  ColumnMapping,
} from '@/lib/csvParser';
import { parseExcel, isExcelFile } from '@/lib/excelParser';
import { getSubscriptionTypes } from '@/services/edgeFunctionWrappers/subscriptionTypesWrapper';
import { createSubscription as createSubscriptionViaWrapper } from '@/services/edgeFunctionWrappers/subscriptionsManagementWrapper';
import { SubscriptionType } from '@/types/subscriptionType';
import { format, addDays } from 'date-fns';

export interface ImportClient {
  rowIndex: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emergencyContact: string;
  isValid: boolean;
  errors: string[];
  isDuplicateInFile: boolean;
  existsInDatabase: boolean;
  // Subscription fields
  subscriptionType?: string;       // Raw value from file (PACK name)
  subscriptionTypeId?: string;     // Matched subscription_type ID
  subscriptionDate?: string;       // Raw date from file
  subscriptionDateParsed?: Date;   // Parsed date
  paymentStatus?: string;          // P, NP, G
  subscriptionError?: string;      // Error if pack not found
}

export interface ImportState {
  step: 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';
  rawHeaders: string[];
  rawRows: Record<string, string>[];
  columnMapping: ColumnMapping;
  clients: ImportClient[];
  validCount: number;
  errorCount: number;
  duplicateCount: number;
  existingCount: number;
  progress: number;
  results: {
    success: number;
    failed: number;
    skipped: number;
    errors: { name: string; error: string }[];
  };
}

const initialState: ImportState = {
  step: 'upload',
  rawHeaders: [],
  rawRows: [],
  columnMapping: {},
  clients: [],
  validCount: 0,
  errorCount: 0,
  duplicateCount: 0,
  existingCount: 0,
  progress: 0,
  results: { success: 0, failed: 0, skipped: 0, errors: [] },
};

export function useClientCSVImport() {
  const { t } = useTranslation(['clients']);
  const { gymId } = useUserStore();
  const appName = useAppName();
  const [state, setState] = useState<ImportState>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);

  // Fetch subscription types on mount
  useEffect(() => {
    if (gymId) {
      getSubscriptionTypes(gymId, true).then(types => {
        setSubscriptionTypes(types);
      }).catch(err => {
        console.error('Error fetching subscription types:', err);
      });
    }
  }, [gymId]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const parseFile = useCallback(async (file: File) => {
    setIsLoading(true);
    
    try {
      let headers: string[];
      let rows: Record<string, string>[];
      
      // Detect file type and parse accordingly
      if (isExcelFile(file)) {
        const result = await parseExcel(file);
        headers = result.headers;
        rows = result.rows;
      } else {
        // CSV parsing
        const content = await file.text();
        const result = parseCSV(content);
        headers = result.headers;
        rows = result.rows;
      }
      
      if (headers.length === 0 || rows.length === 0) {
        throw new Error('Empty file');
      }
      
      const mapping = detectColumnMapping(headers);
      
      // Check if we need manual mapping
      const hasRequiredFields = mapping.email && (mapping.firstName || mapping.lastName);
      
      setState(prev => ({
        ...prev,
        rawHeaders: headers,
        rawRows: rows,
        columnMapping: mapping,
        step: hasRequiredFields ? 'preview' : 'mapping',
      }));
      
      if (hasRequiredFields) {
        // CRITICAL: Ensure subscription types are loaded before processing
        let typesToUse = subscriptionTypes;
        if (typesToUse.length === 0 && gymId) {
          console.log('[Import] Subscription types not loaded yet, fetching...');
          typesToUse = await getSubscriptionTypes(gymId, true);
          setSubscriptionTypes(typesToUse);
          console.log('[Import] Loaded subscription types:', typesToUse.map(t => t.name));
        }
        await processClients(rows, mapping, typesToUse);
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionTypes, gymId]);

  const setColumnMapping = useCallback(async (mapping: ColumnMapping) => {
    setState(prev => ({ ...prev, columnMapping: mapping }));
    // Ensure subscription types are loaded before processing
    let typesToUse = subscriptionTypes;
    if (typesToUse.length === 0 && gymId) {
      console.log('[Import] Subscription types not loaded yet, fetching...');
      typesToUse = await getSubscriptionTypes(gymId, true);
      setSubscriptionTypes(typesToUse);
      console.log('[Import] Loaded subscription types:', typesToUse.map(t => t.name));
    }
    await processClients(state.rawRows, mapping, typesToUse);
  }, [state.rawRows, subscriptionTypes, gymId]);

  const matchSubscriptionType = (packName: string, types: SubscriptionType[]): SubscriptionType | undefined => {
    if (!packName) {
      console.log('[matchSubscriptionType] No pack name provided');
      return undefined;
    }
    
    if (types.length === 0) {
      console.warn('[matchSubscriptionType] No subscription types available! Types array is empty.');
      return undefined;
    }
    
    const normalizedPack = packName.toLowerCase().trim();
    console.log(`[matchSubscriptionType] Matching "${packName}" against types:`, types.map(t => t.name));
    
    // Try exact match first (case-insensitive)
    let match = types.find(t => 
      t.name.toLowerCase().trim() === normalizedPack
    );
    
    // Try partial match
    if (!match) {
      match = types.find(t => 
        t.name.toLowerCase().includes(normalizedPack) ||
        normalizedPack.includes(t.name.toLowerCase())
      );
    }
    
    if (match) {
      console.log(`[matchSubscriptionType] ✓ Matched "${packName}" → "${match.name}" (ID: ${match.id})`);
    } else {
      console.warn(`[matchSubscriptionType] ✗ No match found for "${packName}"`);
    }
    
    return match;
  };

  const processClients = async (rows: Record<string, string>[], mapping: ColumnMapping, types: SubscriptionType[]) => {
    console.log('[processClients] Starting with', rows.length, 'rows and', types.length, 'subscription types');
    setIsLoading(true);
    
    try {
      const clients: ImportClient[] = [];
      const emailsSeen = new Set<string>();
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const errors: string[] = [];
        
        // Extract values using mapping
        let firstName = mapping.firstName ? cleanName(row[mapping.firstName] || '') : '';
        let lastName = mapping.lastName ? cleanName(row[mapping.lastName] || '') : '';
        const email = (mapping.email ? row[mapping.email] || '' : '').trim().toLowerCase();
        const phone = normalizePhoneNumber(mapping.phone ? row[mapping.phone] || '' : '');
        const emergencyContact = normalizePhoneNumber(mapping.emergencyContact ? row[mapping.emergencyContact] || '' : '');
        
        // Subscription fields
        const subscriptionType = mapping.subscriptionType ? (row[mapping.subscriptionType] || '').trim() : '';
        const subscriptionDateRaw = mapping.subscriptionDate ? (row[mapping.subscriptionDate] || '').trim() : '';
        const paymentStatus = mapping.paymentStatus ? (row[mapping.paymentStatus] || '').trim().toUpperCase() : '';
        
        // Handle combined name column
        if (firstName && !lastName && firstName.includes(' ')) {
          const parts = firstName.split(/\s+/);
          if (parts.length >= 2) {
            firstName = cleanName(parts[0]);
            lastName = cleanName(parts.slice(1).join(' '));
          }
        }
        
        // Validation - email with detailed error messages
        if (!email) {
          errors.push(t('import.validation.emailRequired'));
        } else {
          const emailValidation = validateEmail(email);
          if (!emailValidation.isValid && emailValidation.errorCode) {
            const errorKey = `import.validation.email${emailValidation.errorCode.charAt(0).toUpperCase()}${emailValidation.errorCode.slice(1)}`;
            errors.push(t(errorKey, { defaultValue: t('import.validation.emailInvalid') }));
          }
        }
        
        if (!firstName && !lastName) {
          errors.push(t('import.validation.nameRequired'));
        }
        
        // Check duplicate in file
        const isDuplicateInFile = emailsSeen.has(email);
        if (email && isDuplicateInFile) {
          errors.push(t('import.validation.duplicateEmail'));
        }
        
        if (email) {
          emailsSeen.add(email);
        }
        
        // Match subscription type
        let subscriptionTypeId: string | undefined;
        let subscriptionError: string | undefined;
        
        if (subscriptionType) {
          const matchedType = matchSubscriptionType(subscriptionType, types);
          if (matchedType) {
            subscriptionTypeId = matchedType.id;
            console.log(`[processClients] Row ${i}: Pack "${subscriptionType}" matched to "${matchedType.name}"`);
          } else {
            subscriptionError = t('import.validation.subscriptionTypeNotFound');
            console.warn(`[processClients] Row ${i}: Pack "${subscriptionType}" NOT FOUND in types`);
          }
        }
        
        // Parse date
        let subscriptionDateParsed: Date | undefined;
        if (subscriptionDateRaw) {
          const parsed = parseDate(subscriptionDateRaw);
          if (parsed) {
            subscriptionDateParsed = parsed;
          } else {
            // Not a blocking error, just warn
            subscriptionError = subscriptionError || t('import.validation.invalidDate');
          }
        }
        
        clients.push({
          rowIndex: i,
          firstName,
          lastName,
          email,
          phone,
          emergencyContact,
          isValid: errors.length === 0 && !isDuplicateInFile,
          errors,
          isDuplicateInFile,
          existsInDatabase: false,
          subscriptionType,
          subscriptionTypeId,
          subscriptionDate: subscriptionDateRaw,
          subscriptionDateParsed,
          paymentStatus,
          subscriptionError,
        });
      }
      
      // Check existing emails in database
      const validEmails = clients
        .filter(c => c.email && isValidEmail(c.email))
        .map(c => c.email);
      
      if (validEmails.length > 0 && gymId) {
        const existingEmails = await checkExistingEmails(validEmails, gymId);
        
        for (const client of clients) {
          if (existingEmails.includes(client.email)) {
            client.existsInDatabase = true;
            // Ne pas invalider si le client a un pack à assigner (l'abonnement sera créé)
            if (!client.subscriptionTypeId) {
              client.errors.push(t('import.validation.existsInDatabaseNoSubscription'));
              client.isValid = false;
            }
            // Si subscriptionTypeId existe, on garde isValid = true pour créer l'abonnement
          }
        }
      }
      
      // Calculate stats
      const validCount = clients.filter(c => c.isValid).length;
      const errorCount = clients.filter(c => c.errors.length > 0 && !c.isDuplicateInFile && !c.existsInDatabase).length;
      const duplicateCount = clients.filter(c => c.isDuplicateInFile).length;
      const existingCount = clients.filter(c => c.existsInDatabase).length;
      
      setState(prev => ({
        ...prev,
        clients,
        validCount,
        errorCount,
        duplicateCount,
        existingCount,
        step: 'preview',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const checkExistingEmails = async (emails: string[], gymId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase.functions.invoke('client-management', {
        body: {
          action: 'check_existing_emails',
          gym_id: gymId,
          emails,
        },
      });
      
      if (error) {
        console.error('Error checking existing emails:', error);
        return [];
      }
      
      return data?.existing_emails || [];
    } catch (error) {
      console.error('Error checking existing emails:', error);
      return [];
    }
  };

  const updateClient = useCallback((rowIndex: number, field: keyof ImportClient, value: string) => {
    setState(prev => {
      const updatedClients = [...prev.clients];
      const clientIndex = updatedClients.findIndex(c => c.rowIndex === rowIndex);
      
      if (clientIndex === -1) return prev;
      
      const client = { ...updatedClients[clientIndex] };
      
      // Update the field
      if (field === 'firstName' || field === 'lastName') {
        client[field] = cleanName(value);
      } else if (field === 'email') {
        client[field] = value.trim().toLowerCase();
      } else if (field === 'phone' || field === 'emergencyContact') {
        client[field] = normalizePhoneNumber(value);
      } else if (field === 'subscriptionType') {
        client.subscriptionType = value;
        // Re-match subscription type
        const matchedType = matchSubscriptionType(value, subscriptionTypes);
        if (matchedType) {
          client.subscriptionTypeId = matchedType.id;
          client.subscriptionError = undefined;
        } else if (value) {
          client.subscriptionTypeId = undefined;
          client.subscriptionError = t('import.validation.subscriptionTypeNotFound');
        }
      } else if (field === 'subscriptionDate') {
        client.subscriptionDate = value;
        const parsed = parseDate(value);
        client.subscriptionDateParsed = parsed || undefined;
      } else if (field === 'paymentStatus') {
        client.paymentStatus = value.toUpperCase();
      }
      
      // Re-validate the client
      const errors: string[] = [];
      
      if (!client.email) {
        errors.push(t('import.validation.emailRequired'));
      } else {
        const emailValidation = validateEmail(client.email);
        if (!emailValidation.isValid && emailValidation.errorCode) {
          const errorKey = `import.validation.email${emailValidation.errorCode.charAt(0).toUpperCase()}${emailValidation.errorCode.slice(1)}`;
          errors.push(t(errorKey, { defaultValue: t('import.validation.emailInvalid') }));
        }
      }
      
      if (!client.firstName && !client.lastName) {
        errors.push(t('import.validation.nameRequired'));
      }
      
      // Check if still duplicate (based on other emails)
      const otherEmails = updatedClients
        .filter((_, i) => i !== clientIndex)
        .map(c => c.email);
      const isDuplicateInFile = client.email !== '' && otherEmails.includes(client.email);
      
      if (isDuplicateInFile) {
        errors.push(t('import.validation.duplicateEmail'));
      }
      
      // existsInDatabase: ne bloquer que si pas de pack à créer
      if (client.existsInDatabase && !client.subscriptionTypeId) {
        errors.push(t('import.validation.existsInDatabaseNoSubscription'));
      }
      
      client.errors = errors;
      client.isDuplicateInFile = isDuplicateInFile;
      client.isValid = errors.length === 0;
      
      updatedClients[clientIndex] = client;
      
      // Recalculate stats
      const validCount = updatedClients.filter(c => c.isValid).length;
      const errorCount = updatedClients.filter(c => c.errors.length > 0 && !c.isDuplicateInFile && !c.existsInDatabase).length;
      const duplicateCount = updatedClients.filter(c => c.isDuplicateInFile).length;
      const existingCount = updatedClients.filter(c => c.existsInDatabase).length;
      
      return {
        ...prev,
        clients: updatedClients,
        validCount,
        errorCount,
        duplicateCount,
        existingCount,
      };
    });
  }, [t, subscriptionTypes]);

  /**
   * Crée l'abonnement d'une ligne importée.
   *
   * Renvoie `null` en cas de succès, sinon le message d'échec. Le message du
   * SERVEUR doit remonter tel quel : il porte le vrai motif (validation,
   * droits…) et permet à l'administrateur de comprendre quelle ligne corriger.
   * Depuis le cumul par planning, la création n'échoue plus sur un « conflit de
   * planning » — un abonnement du même planning EXPIRE l'ancien et prend sa
   * place (renouvellement), un planning disjoint cohabite.
   */
  const createSubscription = async (userId: string, client: ImportClient): Promise<string | null> => {
    console.log('[createSubscription] Called for user:', userId, {
      subscriptionType: client.subscriptionType,
      subscriptionTypeId: client.subscriptionTypeId,
      gymId
    });
    
    if (!gymId || !client.subscriptionTypeId) {
      console.log('[createSubscription] SKIPPED - No gymId or subscriptionTypeId');
      return null; // No subscription to create
    }
    
    const subscriptionType = subscriptionTypes.find(t => t.id === client.subscriptionTypeId);
    if (!subscriptionType) {
      console.error('[createSubscription] Type not found for ID:', client.subscriptionTypeId);
      return null;
    }
    
    // Calculate dates
    const startDate = client.subscriptionDateParsed || new Date();
    const validityDays = subscriptionType.validity_days || 30;
    const endDate = addDays(startDate, validityDays);
    
    // Determine status based on payment
    let status = 'active';
    if (client.paymentStatus === 'NP') {
      status = 'pending';
    } else if (client.paymentStatus === 'G') {
      status = 'active'; // Gift = active but free
    }
    
    console.log('[createSubscription] Creating subscription:', {
      typeName: subscriptionType.name,
      sessions: subscriptionType.sessions_total,
      validity: validityDays,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      status
    });
    
    try {
      await createSubscriptionViaWrapper({
        user_id: userId,
        gym_id: gymId,
        subscription_type_id: subscriptionType.id,
        type: subscriptionType.name,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        sessions_total: subscriptionType.sessions_total,
        sessions_remaining: subscriptionType.sessions_total,
        jokers_total: subscriptionType.jokers_total || 0,
        jokers_remaining: subscriptionType.jokers_total || 0,
        price: client.paymentStatus === 'G' ? 0 : subscriptionType.price,
        status,
        is_manually_activated: true,
      });
      
      console.log('[createSubscription] ✓ Subscription created successfully for', client.email);
      return null;
    } catch (error: any) {
      console.error('[createSubscription] ✗ Error creating subscription:', error);
      return error?.message || 'Unknown error';
    }
  };

  const startImport = useCallback(async () => {
    if (!gymId) return;
    
    const clientsToImport = state.clients.filter(c => c.isValid);
    if (clientsToImport.length === 0) return;
    
    setState(prev => ({
      ...prev,
      step: 'importing',
      progress: 0,
      results: {
        success: 0,
        failed: 0,
        skipped: prev.clients.length - prev.clients.filter((c) => c.isValid).length,
        errors: [],
      },
    }));
    
    const results = {
      success: 0,
      failed: 0,
      // Lignes invalides écartées avant l'import : le compteur restait à 0
      // et le récapitulatif annonçait « 0 ignorés » même quand la moitié du
      // fichier avait été mise de côté.
      skipped: state.clients.length - clientsToImport.length,
      errors: [] as { name: string; error: string }[],
    };
    
    for (let i = 0; i < clientsToImport.length; i++) {
      const client = clientsToImport[i];
      const fullName = `${client.firstName} ${client.lastName}`.trim();
      
      try {
        const { data, error } = await supabase.functions.invoke('create-user', {
          body: {
            email: client.email,
            first_name: client.firstName,
            last_name: client.lastName,
            phone: client.phone || undefined,
            emergency_contact: client.emergencyContact || undefined,
            role: 'client',
            gym_id: gymId,
            app_name: appName,
          },
        });
        
        if (error) {
          throw new Error(error.message || 'Unknown error');
        }
        
        if (!data?.success) {
          throw new Error(data?.error || 'Failed to create user');
        }
        
        // Create subscription if we have subscription data
        // CORRECTION: create-user retourne user_id (snake_case), pas userId
        const userId = data.user_id || data.userId;
        if (client.subscriptionTypeId && userId) {
          // `createSubscription` rend le message d'échec sans lever : ignorer
          // le retour faisait compter la ligne en succès complet alors que le
          // compte existait sans abonnement. Le message du serveur est
          // concaténé au libellé générique — il porte le vrai motif (validation
          // `sessions_total`, droits d'accès…). Le cumul n'échoue plus : un
          // abonnement du même planning EXPIRE l'ancien et prend sa place.
          const subscriptionError = await createSubscription(userId, client);
          if (subscriptionError) {
            const generic = t('import.subscriptionNotCreated', {
              defaultValue: "Compte créé, mais l'abonnement n'a pas pu être créé",
            });
            results.errors.push({
              name: fullName || client.email,
              error: `${generic} : ${subscriptionError}`,
            });
          }
        }

        results.success++;
      } catch (error: any) {
        results.failed++;
        results.errors.push({
          name: fullName || client.email,
          error: error.message || 'Unknown error',
        });
      }
      
      // Update progress
      const progress = Math.round(((i + 1) / clientsToImport.length) * 100);
      setState(prev => ({ ...prev, progress, results: { ...results } }));
      
      // Delay between requests to avoid rate limiting
      if (i < clientsToImport.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    setState(prev => ({
      ...prev,
      step: 'complete',
      progress: 100,
      results,
    }));
  }, [gymId, state.clients, subscriptionTypes, t]);

  return {
    state,
    isLoading,
    reset,
    parseFile,
    setColumnMapping,
    startImport,
    updateClient,
    subscriptionTypes,
  };
}
